import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAppUpdate, type UpdatePlatform } from '../api/appUpdate';
import { UPDATE_NOTIFICATIONS_KEY, readUpdateNotice, shouldNotifyUpdate, updateNoticeKey, updateNotificationsEnabled, type UpdateNotice } from '../lib/updateNotification';

/** В Web нет устанавливаемой версии: предлагаем нативный релиз, не блокируя браузер по minSupported. */
export default function UpdateAvailableBanner({ onOpenAbout }: { onOpenAbout: (platform: UpdatePlatform) => void }) {
  const dismissed = useRef(false);
  const { t } = useTranslation('misc');
  const [version, setVersion] = useState<string | null>(null);
  const [platform] = useState<UpdatePlatform>(() => /android/i.test(navigator.userAgent) ? 'android' : 'desktop');
  useEffect(() => {
    let cancelled = false;
    let checking = false;
    let visible: string | null = null;
    let enabled = updateNotificationsEnabled();
    let last: UpdateNotice | null = readUpdateNotice(platform);
    const check = async () => {
      if (checking || !enabled || document.visibilityState === 'hidden') return;
      checking = true;
      try {
        const info = await getAppUpdate(platform);
        if (cancelled || !enabled) return;
        // Видимый баннер не создаём заново на каждом опросе; запись общая для вкладок.
        const stored = readUpdateNotice(platform);
        if (stored && (!last || stored.at > last.at)) last = stored;
        if (visible === info.latest && !dismissed.current) return;
        if (shouldNotifyUpdate(info.latest, last, Date.now(), enabled)) {
          last = { version: info.latest, at: Date.now() };
          try { localStorage.setItem(updateNoticeKey(platform), JSON.stringify(last)); }
          catch { /* В приватном режиме дедупликация остаётся в памяти. */ }
          dismissed.current = false;
          visible = info.latest;
          setVersion(visible);
        } else { visible = null; setVersion(null); }
      } catch { /* Ошибка проверки не мешает пользоваться чатами; следующий опрос повторит запрос. */ }
      finally { checking = false; }
    };
    const preference = (event: Event) => {
      enabled = event instanceof CustomEvent ? Boolean(event.detail) : updateNotificationsEnabled();
      if (!enabled) { visible = null; setVersion(null); }
      else void check();
    };
    const storage = (event: StorageEvent) => {
      if (event.key === null || event.key === updateNoticeKey(platform)) {
        last = readUpdateNotice(platform); visible = null; setVersion(null);
      }
      if (event.key === null || event.key === UPDATE_NOTIFICATIONS_KEY) preference(event);
    };
    const resume = () => { void check(); };
    void check();
    const timer = window.setInterval(resume, 60 * 60 * 1000);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('storage', storage);
    window.addEventListener('hc-update-notifications', preference);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('storage', storage);
      window.removeEventListener('hc-update-notifications', preference);
    };
  }, [platform]);
  if (!version) return null;
  return <aside className="update-available-banner" role="status">
    <button type="button" className="btn" onClick={() => { dismissed.current = true; setVersion(null); onOpenAbout(platform); }}>
      {t('updateAvailable', { version, platform: platform === 'android' ? 'Android' : 'Windows' })}
    </button>
    <button type="button" className="btn btn-ghost" aria-label={t('updateDismiss')} onClick={() => { dismissed.current = true; setVersion(null); }}>×</button>
  </aside>;
}
